/**
 * sl-9qm.3.4: ViewConfigStore — Y.js-backed collaborative ViewConfig storage
 *
 * SOLID:
 * - S: Only manages ViewConfig persistence in Y.js
 * - O: Extensible via change events (consumers subscribe, don't modify)
 * - D: Depends on DocService abstraction, not Y.js internals directly
 *
 * The ViewConfig is stored as a Y.js Map at 'sl:viewConfig' inside the Doc's yDoc.
 * This enables real-time collaboration: multiple users can edit the AppBase
 * simultaneously, with Y.js CRDTs resolving conflicts automatically.
 */
import type {
  CustomPropertyMap,
  DocPageSnapshot,
  PageShape,
  ViewConfigSnapshotInput,
} from '@streamlain/view-config';
import {
  BUILDER_BUDGETS,
  BuilderBudgetExceededError,
  PAGE_SHAPE_ROOT_ID,
  parsePageShape,
  viewConfigToDocPageSnapshot,
} from '@streamlain/view-config';
import { LiveData, Store, yjsObserveDeep } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { map } from 'rxjs';
import { type Map as YMap, UndoManager } from 'yjs';

import type { DocService } from '../../doc';
import type {
  DataBindingConfig,
  ViewComponentInstance,
  ViewConfig,
  ViewConfigChangeEvent,
  ViewConfigLayout,
  ViewConfigSettings,
} from '../types/view-config.types';
import { deepEqualSnapshot } from './snapshot-equality';

/** Y.js Map key for ViewConfig data (legacy flat path; kept for rollback). */
const VIEW_CONFIG_MAP_KEY = 'sl:viewConfig';

/**
 * sl-pbux.1.2: Y.js Map key for the DocPageSnapshot tree (new path).
 * Stored under the same yDoc as a sibling of VIEW_CONFIG_MAP_KEY.
 */
const DOC_PAGE_SNAPSHOT_KEY = 'sl:docPageSnapshot';

/**
 * sl-pbux.1.2: Migration state machine key.
 * Map shape:
 *   { status: 'pending' | 'committed' | 'aborted',
 *     migratedAt: ISO-string,
 *     migratedFromKey: 'sl:viewConfig',
 *     migrationVersion: 1 }
 */
const MIGRATION_COMMIT_KEY = 'sl:migrationCommit';
const MIGRATION_VERSION = 1 as const;

type MigrationStatus = 'pending' | 'committed' | 'aborted';
type PageShapeSlot = NonNullable<ViewComponentInstance['slotKey']>;

interface MigrationCommit {
  status: MigrationStatus;
  migratedAt: string;
  migratedFromKey: string;
  migrationVersion: number;
}

/** Default layout for new ViewConfigs */
const DEFAULT_LAYOUT: ViewConfigLayout = {
  type: 'grid',
  columns: 12,
  gap: 16,
  padding: 24,
};

/** Default settings for new ViewConfigs */
const DEFAULT_SETTINGS: ViewConfigSettings = {
  title: '',
  description: '',
};

/**
 * Deserialize the Y.js Map into a typed ViewConfig.
 * Returns null if the map has no config id (not yet initialized).
 */
function deserializeConfig(yMap: YMap<unknown>): ViewConfig | null {
  const id = yMap.get('id') as string | undefined;
  if (!id) return null;

  const layout =
    (yMap.get('layout') as ViewConfigLayout | undefined) ?? DEFAULT_LAYOUT;
  const components =
    (yMap.get('components') as ViewComponentInstance[] | undefined) ?? [];
  const settings =
    (yMap.get('settings') as ViewConfigSettings | undefined) ??
    DEFAULT_SETTINGS;

  return { id, layout, components, settings };
}

/**
 * sl-pbux.8 (audit-fix): Marker passed to every authoring `yDoc.transact`
 * so the `Y.UndoManager` only captures mutations that originated in this
 * store. Remote sync updates carry the provider's origin and are correctly
 * skipped; unrelated services that share the yDoc no longer leak into the
 * Builder's undo stack.
 */
const VIEW_CONFIG_ORIGIN = Symbol('sl-pbux/view-config-origin');

/**
 * sl-pbux.8 (audit-fix-round-2): Distinct origin marker for system-driven
 * writes (initial migration commit + crash-recovery reconciliation). These
 * are not authoring steps and must not appear on the user's undo stack,
 * otherwise Cmd+Z immediately after open would replay or revert a migration
 * the user never asked for. The UndoManager only tracks `VIEW_CONFIG_ORIGIN`
 * so writes tagged with `MIGRATION_ORIGIN` are intentionally invisible to
 * undo/redo.
 */
const MIGRATION_ORIGIN = Symbol('sl-pbux/migration-origin');

/**
 * sl-pbux.8 (audit-fix): Count user-authored element nodes in a DocPageSnapshot.
 *
 * Excludes the constant `PAGE_SHAPE_ROOT_ID` infra node and the per-shape
 * slot nodes (slot-type, not element-type) so the budget threshold is
 * comparable across both the snapshot write path and the
 * `components.length` add-time path. Without this offset a 100-component
 * site would already trip a 100-node soft-warn at the moment the slots
 * are materialised.
 */
function countUserElementNodes(snapshot: DocPageSnapshot): number {
  let count = 0;
  for (const [id, node] of Object.entries(snapshot.nodes)) {
    if (node.type === 'element' && id !== PAGE_SHAPE_ROOT_ID) count++;
  }
  return count;
}

export class ViewConfigStore extends Store {
  private readonly yMap: YMap<unknown>;
  private readonly snapshotMap: YMap<unknown>;
  private readonly migrationMap: YMap<unknown>;
  private readonly undoManager: UndoManager;

  /** Reactive ViewConfig — null when not yet initialized */
  readonly config$: LiveData<ViewConfig | null>;

  /**
   * sl-pbux.8 (audit-fix): Reactive PageShape derived from
   * `settings.pageShape`. Returns `null` when the empty-state picker has
   * not yet committed a shape — Builder surfaces use this to short-circuit
   * snapshot construction. Single SoT for parsing across Layers-Tree,
   * Tree-Canvas, validation hook and the app-base-builder panel.
   */
  readonly pageShape$: LiveData<PageShape | null>;

  /**
   * sl-pbux.8 (audit-fix): Reactive DocPageSnapshot derived from `config$`
   * and `pageShape$`. Single rebuild per Y.js commit, shared by all
   * subscribers; replaces the per-component `useMemo` rebuilds that used
   * to fire 2-3× per render.
   *
   * Returns `null` until both `config$` and `pageShape$` resolve.
   */
  readonly snapshot$: LiveData<DocPageSnapshot | null>;

  /** Change events for consumers (e.g. Live Preview in sl-9qm.6) */
  readonly changes$ = new LiveData<ViewConfigChangeEvent | null>(null);

  /**
   * sl-pbux.8.2: Reactive undo/redo availability for the Builder topbar.
   * Updated synchronously by `Y.UndoManager` stack events so the buttons
   * never lag behind the underlying stack state.
   */
  readonly canUndo$: LiveData<boolean>;
  readonly canRedo$: LiveData<boolean>;

  private readonly stackListener: () => void;

  constructor(private readonly docService: DocService) {
    super();

    const yDoc = this.docService.doc.yDoc;
    this.yMap = yDoc.getMap(VIEW_CONFIG_MAP_KEY);
    this.snapshotMap = yDoc.getMap(DOC_PAGE_SNAPSHOT_KEY);
    this.migrationMap = yDoc.getMap(MIGRATION_COMMIT_KEY);

    // Set up UndoManager tracking the legacy map AND the snapshot map so the
    // post-migration mutation path is undoable in the same step.
    // sl-pbux.8 (audit-fix): scope the UndoManager to mutations carrying
    // VIEW_CONFIG_ORIGIN so unrelated transacts on the same yDoc are not
    // captured. Y.UndoManager default trackedOrigins is `new Set([null])`,
    // which would track ANY untagged transact, including writes from
    // sibling services that happen to share the doc.
    this.undoManager = new UndoManager([this.yMap, this.snapshotMap], {
      trackedOrigins: new Set([VIEW_CONFIG_ORIGIN]),
    });

    this.canUndo$ = new LiveData<boolean>(false);
    this.canRedo$ = new LiveData<boolean>(false);
    this.stackListener = () => {
      this.canUndo$.next(this.undoManager.undoStack.length > 0);
      this.canRedo$.next(this.undoManager.redoStack.length > 0);
    };
    this.undoManager.on('stack-item-added', this.stackListener);
    this.undoManager.on('stack-item-popped', this.stackListener);
    this.undoManager.on('stack-cleared', this.stackListener);

    // Reactive config$ backed by Y.js observeDeep
    this.config$ = LiveData.from<ViewConfig | null>(
      yjsObserveDeep(this.yMap).pipe(map(() => deserializeConfig(this.yMap))),
      deserializeConfig(this.yMap)
    );

    this.pageShape$ = LiveData.computed(get => {
      const config = get(this.config$);
      return config
        ? parsePageShape((config.settings as { pageShape?: unknown }).pageShape)
        : null;
    });

    this.snapshot$ = LiveData.computed(get => {
      const config = get(this.config$);
      const shape = get(this.pageShape$);
      if (!config || !shape) return null;
      return viewConfigToDocPageSnapshot({
        viewConfig: { components: config.components },
        pageShape: shape,
        workspaceId: config.id,
        docId: config.id,
        title: config.settings.title ?? '',
        // Empty string is the canonical "derived-from-LiveData" marker —
        // these snapshots are tree-shape-only and never persisted; the
        // persistence path goes through `writeSnapshot` with a real ISO date.
        materializedAt: '',
      });
    });
  }

  /**
   * sl-pbux.8 (audit-fix): Run an authoring mutation under the local
   * UndoManager origin. Centralises the "everything is undoable + scoped
   * to this store" contract.
   */
  private transactLocal(fn: () => void): void {
    this.yMap.doc?.transact(fn, VIEW_CONFIG_ORIGIN);
  }

  /**
   * sl-pbux.8 (audit-fix-round-2): Run a system-driven write (migration
   * commit, crash-recovery reconciliation) under a separate origin so the
   * UndoManager skips it. Keeps the user's undo stack focused on authoring
   * steps.
   */
  private transactSystem(fn: () => void): void {
    this.yMap.doc?.transact(fn, MIGRATION_ORIGIN);
  }

  /**
   * sl-pbux.8 (audit-fix): Hard-block gate shared by every component-add
   * path. Validated BEFORE the Y.transact so the doc + UndoManager remain
   * coherent when the budget is exceeded — the caller sees the throw and
   * the doc is untouched.
   */
  private assertComponentCountBudget(nextCount: number, detail: string): void {
    if (nextCount > BUILDER_BUDGETS.treeNodeCountHardBlock) {
      throw new BuilderBudgetExceededError({
        budget: 'treeNodeCountHardBlock',
        limit: BUILDER_BUDGETS.treeNodeCountHardBlock,
        actual: nextCount,
        detail,
      });
    }
  }

  /**
   * Initialize the ViewConfig if not yet present in the Y.js Doc.
   * Idempotent — skips if config already exists.
   *
   * sl-pbux.8 (audit-fix-round-2): the initial config write is a system
   * action, not an authoring step. Tagged with `MIGRATION_ORIGIN` so the
   * UndoManager (which only tracks `VIEW_CONFIG_ORIGIN`) ignores it. The
   * explicit `clear()` afterwards is belt-and-suspenders against any prior
   * authoring state that happened to land before initialize was called.
   */
  initialize(configId?: string): void {
    if (this.yMap.get('id')) return;

    this.transactSystem(() => {
      this.yMap.set('id', configId ?? nanoid());
      // Server projections can populate this map before assigning its id.
      // Missing identity does not imply missing installed/user-authored data.
      if (!this.yMap.has('layout')) {
        this.yMap.set('layout', { ...DEFAULT_LAYOUT });
      }
      if (!this.yMap.has('components')) {
        this.yMap.set('components', []);
      }
      if (!this.yMap.has('settings')) {
        this.yMap.set('settings', { ...DEFAULT_SETTINGS });
      }
    });
    this.undoManager.clear();
  }

  /**
   * Initialize only after the host document has completed its initial sync.
   *
   * A missing local map before `waitForSyncReady()` resolves is not proof that
   * the document is new: the server may already contain a projected
   * ViewConfig that has not reached this client yet. Writing the empty default
   * during that window would create a competing CRDT value and can erase the
   * projected Marketplace route components on the next merge.
   */
  async initializeAfterSync(configId?: string): Promise<ViewConfig> {
    await this.docService.doc.waitForSyncReady();
    this.initialize(configId);

    const config = this.config$.value;
    if (!config) {
      throw new Error('ViewConfig initialization did not produce a config');
    }
    return config;
  }

  /**
   * Add a component instance to the canvas.
   * Used when dragging from the Component Palette.
   */
  addComponent(instance: Omit<ViewComponentInstance, 'id'>): string {
    // sl-pbux.8 (audit-fix): Validate budget BEFORE the transact so the
    // Y.Doc + UndoManager stay coherent on throw.
    const components = this.getComponents();
    this.assertComponentCountBudget(components.length + 1, 'addComponent');

    const id = nanoid();
    const fullInstance: ViewComponentInstance = { ...instance, id };

    this.transactLocal(() => {
      const current = this.getComponents();
      current.push(fullInstance);
      this.yMap.set('components', current);
    });

    this.emitChange('component-added', id, 'local');
    return id;
  }

  /**
   * Update a component's props (from Config Panel).
   *
   * `undefined` in the patch DELETES the key from the stored props (so the
   * renderer falls back to the component's `defaultConfig`). A shallow
   * spread alone would leave `key: undefined` in the object, which differs
   * from `key: missing` when later consumers run schema-driven defaulting.
   */
  updateComponentProps(
    instanceId: string,
    patch: Record<string, unknown>
  ): void {
    this.transactLocal(() => {
      const components = this.getComponents();
      const index = components.findIndex(c => c.id === instanceId);
      if (index === -1) return;

      const current = components[index];
      const nextProps: Record<string, unknown> = { ...current.props };
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) {
          delete nextProps[key];
        } else {
          nextProps[key] = value;
        }
      }

      components[index] = { ...current, props: nextProps };
      this.yMap.set('components', components);
    });

    this.emitChange('component-updated', instanceId, 'local');
  }

  /**
   * Update a component's data binding configuration (from Data Binding Section).
   */
  updateComponentDataBinding(
    instanceId: string,
    dataBinding: DataBindingConfig
  ): void {
    this.transactLocal(() => {
      const components = this.getComponents();
      const index = components.findIndex(c => c.id === instanceId);
      if (index === -1) return;

      components[index] = {
        ...components[index],
        dataBinding,
      };
      this.yMap.set('components', components);
    });

    this.emitChange('component-updated', instanceId, 'local');
  }

  updateComponentCustomProperties(
    instanceId: string,
    customProperties: CustomPropertyMap | undefined
  ): void {
    this.transactLocal(() => {
      const components = this.getComponents();
      const index = components.findIndex(c => c.id === instanceId);
      if (index === -1) return;

      const current = components[index];
      if (!customProperties || Object.keys(customProperties).length === 0) {
        const { customProperties: _removed, ...next } = current;
        components[index] = next;
      } else {
        components[index] = { ...current, customProperties };
      }
      this.yMap.set('components', components);
    });

    this.emitChange('component-updated', instanceId, 'local');
  }

  /**
   * sl-pbux.8 (audit-fix): Patch the runtime-formula fields on an instance.
   *
   * Semantics for each field:
   *   - omitted from `patch`  -> leave the stored value untouched
   *   - explicit `null`       -> clear (no formula active)
   *   - `Formula` value       -> set
   *
   * The previous implementation coalesced both `null` and `undefined` to
   * `undefined`, dropping the explicit clear signal that the Layers-Tree
   * visibility toggle relies on. We now treat the patch as a sparse map
   * and preserve `null` verbatim so downstream consumers can distinguish
   * "no formula" from "no patch".
   */
  updateComponentRuntimeFormulas(
    instanceId: string,
    patch: Partial<
      Pick<ViewComponentInstance, 'condition' | 'repeat' | 'repeatKey'>
    >
  ): void {
    this.transactLocal(() => {
      const components = this.getComponents();
      const index = components.findIndex(c => c.id === instanceId);
      if (index === -1) return;

      const next = { ...components[index] };
      if ('condition' in patch) next.condition = patch.condition;
      if ('repeat' in patch) next.repeat = patch.repeat;
      if ('repeatKey' in patch) next.repeatKey = patch.repeatKey;
      components[index] = next;
      this.yMap.set('components', components);
    });

    this.emitChange('component-updated', instanceId, 'local');
  }

  /**
   * Remove a component from the canvas.
   */
  removeComponent(instanceId: string): void {
    this.transactLocal(() => {
      const components = this.getComponents();
      const filtered = components.filter(c => c.id !== instanceId);
      this.yMap.set('components', filtered);
    });

    this.emitChange('component-removed', instanceId, 'local');
  }

  /**
   * sl-bpu6.6.2: Reorder a component to a target list index.
   *
   * `targetIndex` is an insert-before index in the ORIGINAL ordering (the
   * value produced by the drop indicator — 0 = top, list.length = bottom).
   * Because we splice the source out before inserting, a downward move must
   * shift the insert point by 1, otherwise the item lands one slot too low.
   *
   * ViewRenderer sorts components by `position.y || position.x`. To persist
   * the new order we rewrite every `position.y` to match its new index.
   * `position.x` is preserved so horizontal layout hints survive.
   */
  reorderComponent(instanceId: string, targetIndex: number): void {
    this.transactLocal(() => {
      const components = this.getComponents();
      const sorted = [...components].sort(
        (a, b) => a.position.y - b.position.y || a.position.x - b.position.x
      );
      const fromIndex = sorted.findIndex(c => c.id === instanceId);
      if (fromIndex === -1) return;

      const rawTarget = Math.max(0, Math.min(targetIndex, sorted.length));
      // insert-before index → post-splice index when moving downward.
      const insertIndex = fromIndex < rawTarget ? rawTarget - 1 : rawTarget;
      if (fromIndex === insertIndex) return;

      const [moved] = sorted.splice(fromIndex, 1);
      sorted.splice(insertIndex, 0, moved);

      const rewritten = sorted.map((c, index) => ({
        ...c,
        position: { ...c.position, y: index },
      }));
      this.yMap.set('components', rewritten);
    });

    this.emitChange('component-updated', instanceId, 'local');
  }

  /**
   * sl-bpu6.6.2: Add a component instance at a specific list index — one
   * Y.js transaction, one undo step. Callers (e.g. palette drop) must not
   * split this into `addComponent` + `reorderComponent`, because that would
   * expose an intermediate "appended at end" state to collaborators.
   *
   * `targetIndex` follows the same insert-before semantic as
   * `reorderComponent`; out-of-bounds values are clamped to `[0, length]`.
   */
  addComponentAt(
    instance: Omit<ViewComponentInstance, 'id'>,
    targetIndex: number
  ): string {
    // sl-pbux.8 (audit-fix): Validate budget BEFORE the transact so the
    // Y.Doc + UndoManager stay coherent on throw.
    const preComponents = this.getComponents();
    this.assertComponentCountBudget(preComponents.length + 1, 'addComponentAt');

    const id = nanoid();

    this.transactLocal(() => {
      const components = this.getComponents();
      const sorted = [...components].sort(
        (a, b) => a.position.y - b.position.y || a.position.x - b.position.x
      );

      const fullInstance: ViewComponentInstance = { ...instance, id };
      const insertIndex = Math.max(0, Math.min(targetIndex, sorted.length));
      sorted.splice(insertIndex, 0, fullInstance);

      const rewritten = sorted.map((c, index) => ({
        ...c,
        position: { ...c.position, y: index },
      }));
      this.yMap.set('components', rewritten);
    });

    this.emitChange('component-added', id, 'local');
    return id;
  }

  /**
   * Replace every component owned by one AppBaseConfig page in a single authoring
   * transaction. Setup templates are the source of truth for their page
   * sections; changing a route type must not leave old form/listing sections
   * behind in the shared ViewConfig.
   */
  replacePageComponents(
    pageId: string,
    instances: readonly Omit<ViewComponentInstance, 'id'>[]
  ): string[] {
    const preComponents = this.getComponents();
    const retainedCount = preComponents.filter(
      component => component.props['pageId'] !== pageId
    ).length;
    this.assertComponentCountBudget(
      retainedCount + instances.length,
      `replacePageComponents ${pageId}`
    );

    const ids = instances.map(() => nanoid());
    this.transactLocal(() => {
      const retained = this.getComponents()
        .filter(component => component.props['pageId'] !== pageId)
        .sort(
          (a, b) => a.position.y - b.position.y || a.position.x - b.position.x
        );
      const replacements = instances.map((instance, index) => ({
        ...instance,
        id: ids[index]!,
      }));
      const rewritten = [...retained, ...replacements].map(
        (component, index) => ({
          ...component,
          position: { ...component.position, y: index },
        })
      );
      this.yMap.set('components', rewritten);
    });

    this.emitChange('component-updated', undefined, 'local');
    return ids;
  }

  /**
   * sl-pbux.3.5: Move a component instance into a different slot or
   * container. Atomic via Y.transact so undo restores the prior placement
   * in one step.
   *
   * Target semantics:
   *   - { kind: 'slot', slotKey } — top-level placement under a page-shape
   *     slot. Clears any prior `parentId`.
   *   - { kind: 'container', parentId } — nested placement inside another
   *     instance (which must be a container component). Clears any prior
   *     `slotKey`.
   *   - { kind: 'top-level' } — promote a nested instance to top-level
   *     under its current slot fallback (clears parentId, keeps slotKey).
   */
  moveNode(
    sourceId: string,
    target:
      | { kind: 'slot'; slotKey: 'header' | 'sidebar' | 'main' | 'footer' }
      | { kind: 'container'; parentId: string }
  ): void {
    this.transactLocal(() => {
      const components = this.getComponents();
      const index = components.findIndex(c => c.id === sourceId);
      if (index === -1) return;
      const current = components[index];
      const next: ViewComponentInstance =
        target.kind === 'slot'
          ? { ...current, slotKey: target.slotKey, parentId: null }
          : { ...current, parentId: target.parentId, slotKey: undefined };
      const nextList = [...components];
      nextList[index] = next;
      this.yMap.set('components', nextList);
    });
    this.emitChange('component-updated', sourceId, 'local');
  }

  /**
   * Move or resize a component on the canvas (drag/resize).
   */
  moveComponent(
    instanceId: string,
    position: Partial<ViewComponentInstance['position']>
  ): void {
    this.transactLocal(() => {
      const components = this.getComponents();
      const index = components.findIndex(c => c.id === instanceId);
      if (index === -1) return;

      components[index] = {
        ...components[index],
        position: { ...components[index].position, ...position },
      };
      this.yMap.set('components', components);
    });

    this.emitChange('component-updated', instanceId, 'local');
  }

  /**
   * Update the canvas layout settings.
   */
  updateLayout(layout: Partial<ViewConfigLayout>): void {
    this.transactLocal(() => {
      const current =
        (this.yMap.get('layout') as ViewConfigLayout | undefined) ??
        DEFAULT_LAYOUT;
      this.yMap.set('layout', { ...current, ...layout });
    });

    this.emitChange('layout-changed', undefined, 'local');
  }

  /**
   * Get a single component by ID.
   */
  getComponent(instanceId: string): ViewComponentInstance | undefined {
    return this.getComponents().find(c => c.id === instanceId);
  }

  /**
   * Undo the last ViewConfig change.
   */
  undo(): void {
    this.undoManager.undo();
  }

  /**
   * Redo the last undone ViewConfig change.
   */
  redo(): void {
    this.undoManager.redo();
  }

  /**
   * Whether undo is available.
   */
  canUndo(): boolean {
    return this.undoManager.undoStack.length > 0;
  }

  /**
   * Whether redo is available.
   */
  canRedo(): boolean {
    return this.undoManager.redoStack.length > 0;
  }

  // =========================================================================
  // sl-pbux.1.2: DocPageSnapshot read/write + migration state machine.
  // =========================================================================

  /**
   * Read the canonical DocPageSnapshot for this view.
   *
   * Order of resolution:
   *  1. If `sl:docPageSnapshot` exists AND `sl:migrationCommit.status === 'committed'`
   *     -> return the persisted tree.
   *  2. If `sl:docPageSnapshot` exists AND status is `pending` -> attempt
   *     crash-recovery (deterministic re-transform + byte-compare). On match
   *     promote to `committed`; on mismatch demote to `aborted` and fall back.
   *  3. Else if `sl:viewConfig` exists -> transient transform-on-read using the
   *     pure function. This is the bridge state used by the renderer until a
   *     first write triggers `ensureMigrated()`.
   *  4. Else -> null (uninitialized view).
   *
   * Pure side: reading never writes. Promotion/demotion of the commit status
   * happens only inside crash-recovery and only when re-transform succeeds.
   */
  readSnapshot(args: {
    pageShape: PageShape;
    workspaceId: string;
    docId: string;
    title: string;
    /** Test-only clock injection. Defaults to `new Date().toISOString()`. */
    materializedAt?: string;
  }): DocPageSnapshot | null {
    const commit = this.readMigrationCommit();
    const storedSnapshot = this.deserializeStoredSnapshot();

    if (storedSnapshot && commit?.status === 'committed') {
      return storedSnapshot;
    }

    if (storedSnapshot && commit?.status === 'pending') {
      const legacyConfig = deserializeConfig(this.yMap);
      if (!legacyConfig) {
        // Pending without a legacy source is unrecoverable — keep the stored
        // snapshot as-is but mark aborted so subsequent writes overwrite it.
        this.writeMigrationCommit({
          status: 'aborted',
          migratedAt: this.nowOr(args.materializedAt),
          migratedFromKey: VIEW_CONFIG_MAP_KEY,
          migrationVersion: MIGRATION_VERSION,
        });
        return storedSnapshot;
      }
      const recovered = viewConfigToDocPageSnapshot({
        viewConfig: this.toSnapshotInput(legacyConfig),
        pageShape: args.pageShape,
        workspaceId: args.workspaceId,
        docId: args.docId,
        title: args.title,
        materializedAt: storedSnapshot.materializedAt,
      });
      // sl-pbux.8 (audit-fix-round-2): structural deep-equal instead of
      // `JSON.stringify` compare. Object key order in the `nodes` Record is
      // not guaranteed to round-trip through Y.Map iteration order — two
      // semantically equal snapshots could fail a bytewise compare and trip
      // a false `aborted` recovery.
      const isMatch = deepEqualSnapshot(recovered, storedSnapshot);
      // sl-pbux.8 (audit-fix-round-2): tag as system origin so the migration
      // commit + map clear are NOT captured by the UndoManager. Without this
      // a user could Cmd+Z immediately after open and replay the recovery
      // step, which is not a meaningful authoring undo.
      this.transactSystem(() => {
        this.writeMigrationCommit({
          status: isMatch ? 'committed' : 'aborted',
          migratedAt: storedSnapshot.materializedAt,
          migratedFromKey: VIEW_CONFIG_MAP_KEY,
          migrationVersion: MIGRATION_VERSION,
        });
        if (!isMatch) {
          this.snapshotMap.clear();
        }
      });
      return isMatch ? storedSnapshot : this.transientTransform(args);
    }

    const transient = this.transientTransform(args);
    return transient;
  }

  /**
   * Persist a DocPageSnapshot to `sl:docPageSnapshot` and mark the commit as
   * `committed`. Replaces the previous snapshot atomically (single transact).
   * Idempotent: writing the same snapshot twice yields the same Y.Map state.
   *
   * Does NOT delete `sl:viewConfig`; rollback to the legacy renderer remains
   * available until the UI layer (sl-pbux.3) cuts the dependency.
   */
  writeSnapshot(snapshot: DocPageSnapshot): void {
    if (!this.yMap.doc) {
      throw new Error('ViewConfigStore: Y.Doc not bound to map');
    }
    // sl-pbux.8 (audit-fix-round-3): fail loud when a caller hands us a
    // transient derived snapshot. `snapshot$` LiveData uses an empty
    // `materializedAt` as the canonical "this is shape-only, not for
    // persistence" marker — letting it through would corrupt the migration
    // commit timestamp downstream.
    if (snapshot.materializedAt === '') {
      throw new Error(
        'ViewConfigStore.writeSnapshot: snapshot.materializedAt is empty — ' +
          'derived-only snapshots (from snapshot$ LiveData) must not be ' +
          'persisted. Materialize a fresh ISO timestamp before writing.'
      );
    }
    // sl-pbux.8.6 + sl-pbux.8 audit-fix: Hard-block writes that exceed the
    // tree-node count budget. Throwing here is fail-loud — the caller stays
    // in the pre-write state so authoring undo remains coherent. Counted in
    // user-component units (snapshot element nodes minus the constant
    // PAGE_SHAPE_ROOT_ID infra node) so the constant has the same semantics
    // here as in the addComponent gate.
    const userComponentCount = countUserElementNodes(snapshot);
    this.assertComponentCountBudget(
      userComponentCount,
      `writeSnapshot ${snapshot.docId}`
    );
    // sl-pbux.8 (audit-fix-round-3): writeSnapshot is a bulk replace driven
    // by external state (migration / backend reconciliation / test
    // fixtures), not user authoring — route through `transactSystem` so it
    // does NOT enter the user's undo stack. The corresponding migration
    // commit-flag write below shares the same origin.
    this.transactSystem(() => {
      this.serializeSnapshot(snapshot);
      this.writeMigrationCommit({
        status: 'committed',
        migratedAt: snapshot.materializedAt,
        migratedFromKey: VIEW_CONFIG_MAP_KEY,
        migrationVersion: MIGRATION_VERSION,
      });
    });
  }

  /**
   * Ensure the view is represented as a DocPageSnapshot. Called once per
   * builder-open before the first tree-aware mutation.
   *
   * Semantics:
   *  - If a committed snapshot already exists -> no-op.
   *  - If a pending snapshot exists -> readSnapshot() runs crash-recovery; if
   *    that aborts, this method re-migrates from the legacy map.
   *  - If only legacy `sl:viewConfig` exists -> deterministic transform +
   *    atomic write under a single Y.transact, with the commit flag flipped
   *    from `pending` to `committed` in the same transaction.
   *  - If neither exists -> creates an empty PageShapeRoot snapshot using
   *    `pageShape`.
   *
   * Single-write-lock: while `sl:migrationCommit.status === 'pending'`, other
   * tabs observing the yDoc see the same intermediate state. Y.transact is
   * the atomic unit; pending status is only visible across transaction
   * boundaries (i.e. across page reloads), which is exactly the crash-recovery
   * case `readSnapshot` handles.
   */
  ensureMigrated(args: {
    pageShape: PageShape;
    workspaceId: string;
    docId: string;
    title: string;
    materializedAt?: string;
  }): DocPageSnapshot {
    if (!this.yMap.doc) {
      throw new Error('ViewConfigStore: Y.Doc not bound to map');
    }

    const existingCommit = this.readMigrationCommit();
    const existingSnapshot = this.deserializeStoredSnapshot();

    if (existingCommit?.status === 'committed' && existingSnapshot) {
      return existingSnapshot;
    }

    // `pending` snapshot recovered by readSnapshot — re-read after recovery.
    if (existingCommit?.status === 'pending' && existingSnapshot) {
      const recovered = this.readSnapshot(args);
      const postRecoveryCommit = this.readMigrationCommit();
      if (postRecoveryCommit?.status === 'committed' && recovered) {
        return recovered;
      }
      // Fall through to a fresh migration when recovery aborted.
    }

    const legacyConfig = deserializeConfig(this.yMap);
    const materializedAt = this.nowOr(args.materializedAt);
    const snapshot = viewConfigToDocPageSnapshot({
      viewConfig: this.toSnapshotInput(legacyConfig),
      pageShape: args.pageShape,
      workspaceId: args.workspaceId,
      docId: args.docId,
      title: args.title,
      materializedAt,
    });

    // sl-pbux.8 (audit-fix): fail-loud if legacy data is already past the
    // hard-block budget. Catching this at migration time (before the first
    // snapshot is materialised) is preferable to letting the Builder load
    // an unbounded tree that subsequent writes would also reject.
    this.assertComponentCountBudget(
      countUserElementNodes(snapshot),
      `ensureMigrated ${args.docId}`
    );

    // sl-pbux.8 (audit-fix-round-2): tag as system origin — the migration
    // write is bookkeeping triggered the first time a Builder opens a
    // legacy site, not an authoring step the user just performed. Putting
    // it on the undo stack would let Cmd+Z roll the migration back, which
    // would corrupt the snapshot/commit invariant.
    this.transactSystem(() => {
      this.writeMigrationCommit({
        status: 'pending',
        migratedAt: materializedAt,
        migratedFromKey: VIEW_CONFIG_MAP_KEY,
        migrationVersion: MIGRATION_VERSION,
      });
      this.serializeSnapshot(snapshot);
      this.writeMigrationCommit({
        status: 'committed',
        migratedAt: materializedAt,
        migratedFromKey: VIEW_CONFIG_MAP_KEY,
        migrationVersion: MIGRATION_VERSION,
      });
    });

    return snapshot;
  }

  /** Inspector for tests and tooling — current migration state, never mutates. */
  getMigrationCommit(): MigrationCommit | null {
    return this.readMigrationCommit();
  }

  /**
   * sl-pbux.2.1: Builder Empty-State + Page-Settings panel write the page
   * shape via this method. Idempotent — writing the same shape twice is a
   * structural no-op.
   *
   * sl-pbux.8 (audit-fix-round-2): the synchronous `getPageShape()` getter
   * was removed because it diverged from `pageShape$` on the empty-state
   * fallback (getter returned `'header'`, LiveData returns `null`). All
   * consumers now read via `pageShape$`, which is the single source of
   * truth for the UI's "has the picker committed?" semantics.
   */
  setPageShape(
    shape: PageShape,
    options?: { moveDroppedSlotsToMain?: readonly PageShapeSlot[] }
  ): void {
    const droppedSlots = new Set(options?.moveDroppedSlotsToMain ?? []);
    const shouldMoveDroppedSlots = droppedSlots.size > 0;
    this.transactLocal(() => {
      const current =
        (this.yMap.get('settings') as ViewConfigSettings | undefined) ??
        DEFAULT_SETTINGS;
      this.yMap.set('settings', { ...current, pageShape: shape });
      if (shouldMoveDroppedSlots) {
        this.yMap.set(
          'components',
          this.getComponents().map(component =>
            component.slotKey && droppedSlots.has(component.slotKey)
              ? { ...component, slotKey: 'main', parentId: null }
              : component
          )
        );
      }
    });
    this.emitChange('settings-changed', undefined, 'local');
    if (shouldMoveDroppedSlots) {
      this.emitChange('component-updated', undefined, 'local');
    }
  }

  /** Inspector for tests — raw stored snapshot or null. */
  getStoredSnapshot(): DocPageSnapshot | null {
    return this.deserializeStoredSnapshot();
  }

  private transientTransform(args: {
    pageShape: PageShape;
    workspaceId: string;
    docId: string;
    title: string;
    materializedAt?: string;
  }): DocPageSnapshot | null {
    const legacyConfig = deserializeConfig(this.yMap);
    if (!legacyConfig) return null;
    return viewConfigToDocPageSnapshot({
      viewConfig: this.toSnapshotInput(legacyConfig),
      pageShape: args.pageShape,
      workspaceId: args.workspaceId,
      docId: args.docId,
      title: args.title,
      materializedAt: this.nowOr(args.materializedAt),
    });
  }

  private toSnapshotInput(config: ViewConfig | null): ViewConfigSnapshotInput {
    if (!config) return { components: [] };
    return { components: config.components };
  }

  private nowOr(materializedAt: string | undefined): string {
    return materializedAt ?? new Date().toISOString();
  }

  private serializeSnapshot(snapshot: DocPageSnapshot): void {
    this.snapshotMap.clear();
    this.snapshotMap.set('snapshotVersion', snapshot.snapshotVersion);
    this.snapshotMap.set('contentType', snapshot.contentType);
    this.snapshotMap.set('workspaceId', snapshot.workspaceId);
    this.snapshotMap.set('docId', snapshot.docId);
    this.snapshotMap.set('rootNodeId', snapshot.rootNodeId);
    this.snapshotMap.set('title', snapshot.title);
    this.snapshotMap.set('materializedAt', snapshot.materializedAt);
    this.snapshotMap.set('nodes', snapshot.nodes);
  }

  private deserializeStoredSnapshot(): DocPageSnapshot | null {
    const rootNodeId = this.snapshotMap.get('rootNodeId') as string | undefined;
    if (!rootNodeId) return null;
    return {
      snapshotVersion: this.snapshotMap.get('snapshotVersion') as 1,
      contentType: this.snapshotMap.get('contentType') as 'doc_page',
      workspaceId: this.snapshotMap.get('workspaceId') as string,
      docId: this.snapshotMap.get('docId') as string,
      rootNodeId,
      title: this.snapshotMap.get('title') as string,
      materializedAt: this.snapshotMap.get('materializedAt') as string,
      nodes: this.snapshotMap.get('nodes') as DocPageSnapshot['nodes'],
    };
  }

  private readMigrationCommit(): MigrationCommit | null {
    const status = this.migrationMap.get('status') as
      | MigrationStatus
      | undefined;
    if (!status) return null;
    return {
      status,
      migratedAt: this.migrationMap.get('migratedAt') as string,
      migratedFromKey: this.migrationMap.get('migratedFromKey') as string,
      migrationVersion: this.migrationMap.get('migrationVersion') as number,
    };
  }

  private writeMigrationCommit(commit: MigrationCommit): void {
    this.migrationMap.set('status', commit.status);
    this.migrationMap.set('migratedAt', commit.migratedAt);
    this.migrationMap.set('migratedFromKey', commit.migratedFromKey);
    this.migrationMap.set('migrationVersion', commit.migrationVersion);
  }

  override dispose(): void {
    this.undoManager.off('stack-item-added', this.stackListener);
    this.undoManager.off('stack-item-popped', this.stackListener);
    this.undoManager.off('stack-cleared', this.stackListener);
    this.undoManager.destroy();
    super.dispose();
  }

  private getComponents(): ViewComponentInstance[] {
    return [
      ...((this.yMap.get('components') as ViewComponentInstance[]) ?? []),
    ];
  }

  private emitChange(
    type: ViewConfigChangeEvent['type'],
    instanceId: string | undefined,
    source: ViewConfigChangeEvent['source']
  ): void {
    this.changes$.next({ type, instanceId, source });
  }
}
